/*
 * RandomCrush variant gallery
 *
 * Image groups are configured per product in Shopify Admin through
 * custom.variant_image_groups, for example:
 * [{"options":["14K White Gold","Clear Crystal"],"images":"1-7"}]
 *
 * This is intentionally the only script that filters product-media groups.
 * It uses Prestige's public carousel.filter API rather than overriding scroll,
 * thumbnail clicks, or browser scrolling.
 */
(() => {
  const normalise = value => String(value || '').trim().toLocaleLowerCase();

  const installCounter = gallery => {
    if (gallery.dataset.rcGalleryPaginationReady === 'true') return;

    const navigation = gallery.querySelector(':scope > carousel-navigation.page-dots');
    if (!navigation) return;

    const counter = document.createElement('div');
    counter.className = 'rc-gallery-pagination';
    const previous = document.createElement('button');
    previous.className = 'rc-gallery-count__arrow rc-gallery-count__arrow--previous';
    previous.type = 'button';
    previous.setAttribute('aria-label', 'Previous image');
    previous.textContent = '‹';
    const value = document.createElement('span');
    value.className = 'rc-gallery-count__value';
    value.setAttribute('aria-live', 'polite');
    value.setAttribute('aria-atomic', 'true');
    const next = document.createElement('button');
    next.className = 'rc-gallery-count__arrow rc-gallery-count__arrow--next';
    next.type = 'button';
    next.setAttribute('aria-label', 'Next image');
    next.textContent = '›';
    counter.append(previous, value, next);
    navigation.insertAdjacentElement('afterend', counter);

    const visibleButtons = () => [...navigation.querySelectorAll('button')]
      .filter(button => !button.hidden);

    const carousel = gallery.querySelector('.product-gallery__carousel');
    const visibleMedia = () => [...(carousel?.querySelectorAll('.product-gallery__media') || [])]
      .filter(item => !item.hidden && getComputedStyle(item).display !== 'none');
    const currentMediaIndex = media => {
      if (!media.length || !carousel) return 0;
      const viewport = carousel.getBoundingClientRect();
      const centre = viewport.left + viewport.width / 2;
      let nearest = 0;
      let distance = Infinity;
      media.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const delta = Math.abs(rect.left + rect.width / 2 - centre);
        if (delta < distance) { nearest = index; distance = delta; }
      });
      return nearest;
    };
    const updateCounter = () => {
      const media = visibleMedia();
      const current = currentMediaIndex(media) + 1;
      const text = `${current}/${Math.max(media.length, 1)}`;
      if (value.textContent !== text) value.textContent = text;
      counter.hidden = media.length <= 1;
    };

    const move = direction => {
      const media = visibleMedia();
      if (media.length <= 1) return;
      if (carousel && matchMedia('(max-width: 999px)').matches) {
        const target = (currentMediaIndex(media) + direction + media.length) % media.length;
        const rect = media[target].getBoundingClientRect();
        const viewport = carousel.getBoundingClientRect();
        carousel.scrollTo({ left: carousel.scrollLeft + rect.left - viewport.left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        return;
      }
      const buttons = visibleButtons();
      if (buttons.length <= 1) return;
      const selected = buttons.findIndex(button => button.getAttribute('aria-current') === 'true');
      const current = selected >= 0 ? selected : 0;
      const target = (current + direction + buttons.length) % buttons.length;
      buttons[target].click();
      requestAnimationFrame(updateCounter);
    };

    let counterFrame = 0;
    const scheduleCounter = () => {
      if (counterFrame) return;
      counterFrame = requestAnimationFrame(() => { counterFrame = 0; updateCounter(); });
    };
    carousel?.addEventListener('scroll', scheduleCounter, { passive: true });
    carousel?.addEventListener('scrollend', scheduleCounter);
    carousel?.addEventListener('load', scheduleCounter, true);
    if (carousel) new MutationObserver(scheduleCounter).observe(carousel, { attributes: true, childList: true, subtree: true, attributeFilter: ['hidden', 'style', 'class'] });
    window.addEventListener('resize', scheduleCounter, { passive: true });
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));

    new MutationObserver(updateCounter).observe(navigation, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['aria-current', 'hidden', 'style', 'class']
    });
    navigation.addEventListener('click', () => requestAnimationFrame(updateCounter));
    gallery.dataset.rcGalleryPaginationReady = 'true';
    updateCounter();
  };

  const positionsFor = value => String(value || '')
    .split(',')
    .flatMap(part => {
      const values = part.trim().split('-').map(Number);
      return values.length === 2
        ? Array.from({ length: values[1] - values[0] + 1 }, (_, index) => values[0] + index)
        : values;
    })
    .filter(Number.isFinite);

  const getPicker = gallery => {
    const formId = gallery.getAttribute('form');
    const scope = gallery.closest('quick-buy-modal, .shopify-section, main, body') || document;
    return [...scope.querySelectorAll('variant-picker[handle]')]
      .find(picker => picker.getAttribute('form') === formId) || null;
  };

  const selectedValues = picker => {
    return [...picker.querySelectorAll('input[type="radio"]:checked')]
      .map(input => normalise(input.value));
  };

  const syncNativeSelection = (picker, values) => {
    const wanted = values.map(normalise);
    const radios = [...picker.querySelectorAll('input[type="radio"]')];
    const inputs = wanted.map(value => radios.find(input => normalise(input.value) === value));
    if (inputs.length !== wanted.length || inputs.some(input => !input)) return;

    let changed = false;
    inputs.forEach(input => {
      if (!input.checked) {
        input.checked = true;
        changed = true;
      }
    });
    // The theme's form controller uses the native change event to resolve the
    // actual purchasable variant, price and Add to Cart state.
    if (changed) inputs.at(-1).dispatchEvent(new Event('change', { bubbles: true }));
  };

  const groupFor = (groups, picker, changedValue) => {
    const selected = selectedValues(picker);
    const exact = groups.find(group => {
      const options = Array.isArray(group.options) ? group.options.map(normalise) : [];
      return options.length === selected.length && options.every(option => selected.includes(option));
    });
    if (exact) return exact;

    // A few legacy products expose an impossible default combination (for
    // example, Yellow Gold + Clear Crystal) even though the sellable groups
    // are White + Clear and Yellow + Amethyst/Smoky Quartz. Resolve it to a
    // real group instead of leaving all media visible and letting the gallery
    // clamp its scroll position.
    const preferred = changedValue && normalise(changedValue);
    const fallback = preferred
      ? groups.find(group => (group.options || []).map(normalise).includes(preferred))
      : groups[0];
    if (!fallback) return null;
    syncNativeSelection(picker, fallback.options || []);
    return fallback;
  };

  const updateAvailableOptions = (picker, groups) => {
    const radios = [...picker.querySelectorAll('input[type="radio"]')];
    radios.forEach(input => {
      const field = input.closest('fieldset');
      const fieldName = field?.querySelector('legend')?.textContent?.trim() || '';
      const label = picker.querySelector(`label[for="${input.id}"]`);
      // Keep Metal choices visible. Choosing a metal is allowed to select its
      // first compatible colour automatically; only the dependent Color /
      // Gemstone field should be narrowed.
      if (!/^(color|gemstone):?$/i.test(fieldName)) {
        if (label) {
          label.hidden = false;
          label.style.removeProperty('display');
        }
        return;
      }
      const otherSelections = radios
        .filter(other => other.name !== input.name && other.checked)
        .map(other => normalise(other.value));
      const available = groups.some(group => {
        const options = (group.options || []).map(normalise);
        return options.includes(normalise(input.value)) && otherSelections.every(value => options.includes(value));
      });
      if (label) {
        label.toggleAttribute('hidden', !available);
        if (available) label.style.removeProperty('display');
      }
    });
  };

  const setPrimary = (gallery, media) => {
    const primaryId = media[0]?.dataset.mediaId;
    gallery.querySelectorAll('.product-gallery__media[data-media-id]').forEach(item => {
      item.classList.toggle('is-initial', item.dataset.mediaId === primaryId);
    });
  };

  const filterGallery = (gallery, picker, groups, resetMobile, changedValue, forceFirstImage = false) => {
    const group = groupFor(groups, picker, changedValue);
    if (!group) return false;
    updateAvailableOptions(picker, groups);

    const allMedia = [...gallery.querySelectorAll('.product-gallery__media[data-media-id]')];
    const visiblePositions = new Set(positionsFor(group.images));
    const visibleMedia = allMedia.filter((_, index) => visiblePositions.has(index + 1));
    if (!visibleMedia.length) return false;

    const key = visibleMedia.map(item => item.dataset.mediaId).join('|');
    const carousel = gallery.querySelector('.product-gallery__carousel');
    // Prestige can restore all cells when variants share one media group.
    // The cached key is valid only while the actual hidden state still matches.
    const filterIsCurrent = allMedia.every((item, index) =>
      item.hidden === !visiblePositions.has(index + 1)
    );
    if (key === gallery.dataset.rcVisibleMedia && filterIsCurrent) {
      // Prestige can select a variant's Shopify featured media after our
      // grouping code has run. Re-select the group's first cell when this is
      // an explicit reset, even though the visible image set is unchanged.
      if (forceFirstImage && carousel && typeof carousel.select === 'function') {
        carousel.select(0, { instant: true });
        const imageList = gallery.querySelector('.product-gallery__image-list');
        if (imageList) imageList.scrollTop = 0;
        setPrimary(gallery, visibleMedia);
      }
      return true;
    }

    if (!carousel || typeof carousel.filter !== 'function') return false;

    // Clear inline leftovers from retired product-specific scripts, then let
    // the theme filter cells, thumbnails and page dots as one system.
    gallery.querySelectorAll('[data-media-id]').forEach(item => {
      item.style.removeProperty('display');
      item.removeAttribute('data-rc-gallery-visible');
      item.removeAttribute('data-rc-lighthouse-visible');
      item.removeAttribute('data-rc-solo-visible');
    });

    carousel.filter(allMedia.reduce((hidden, _, index) => {
      if (!visiblePositions.has(index + 1)) hidden.push(index);
      return hidden;
    }, []));
    // Filtering does not reset Prestige's selected carousel cell. If the
    // prior cell is removed, the browser interpolates to a neighbouring cell
    // and the gallery visibly bounces. Select the first image of the new
    // group immediately, without the theme's smooth-scroll animation.
    carousel.select?.(0, { instant: true });
    // On desktop Prestige uses a separate vertical image rail. Its previous
    // scroll offset can point beyond a newly filtered, shorter group and is
    // then visibly clamped by the browser. Reset only this internal rail to
    // the first image; never scroll the document itself.
    const imageList = gallery.querySelector('.product-gallery__image-list');
    if (imageList) imageList.scrollTop = 0;
    setPrimary(gallery, visibleMedia);
    gallery.dataset.rcVisibleMedia = key;

    return true;
  };

  const blockRetiredControllers = picker => {
    // These flags prevent earlier inline product blocks from registering a
    // second gallery controller after this shared controller has started.
    [
      'rcSingleGroups', 'rcChoker', 'rcSolo', 'rcStuds', 'rcLighthouse',
      'rcLighthouseFixed', 'rcGalleryGeneric', 'rcDewGroups',
      'rcInsightImageGroups', 'rcBreezeBraceletImageGroups',
      'rcBreezeNecklaceImageGroups', 'rcInsightEarringsGroups',
      'rcLighthouseMediaBound'
    ].forEach(key => { picker.dataset[key] = 'true'; });
  };

  const restoreMisappliedNativeOptions = picker => {
    // Legacy product-specific scripts can replace the native controls with a
    // second custom selector. The shared controller owns all grouped galleries,
    // so keep the native picker as the single source of truth.
    if ([
      '14k-gold-necklace-choker',
      'the-movement-stud-14k-gold-earrings'
    ].includes(picker.getAttribute('handle'))) {
      picker.querySelectorAll('.rc-movement-selector, .rc-movement-linked-selectors, .rc-qb-linked').forEach(control => control.remove());
      picker.querySelectorAll('.rc-movement-native').forEach(field => field.classList.remove('rc-movement-native'));
      return;
    }
    picker.querySelectorAll('.rc-movement-linked-selectors, .rc-lighthouse-linked-selectors').forEach(control => control.remove());
    picker.querySelectorAll('.rc-movement-native, .rc-lighthouse-native').forEach(field => {
      field.classList.remove('rc-movement-native', 'rc-lighthouse-native');
    });
  };

  const orderLighthouseMetal = (picker, groups) => {
    if (picker.getAttribute('handle') !== '14k-gold-crystal-necklace') return;
    const metalField = [...picker.querySelectorAll('fieldset')].find(field =>
      /^metal:?$/i.test(field.querySelector('legend')?.textContent?.trim() || '')
    );
    const options = metalField?.querySelector('[data-option-selector]');
    if (!options) return;

    const pairs = [...options.querySelectorAll('input[type="radio"]')].map(input => ({
      input,
      label: options.querySelector(`label[for="${input.id}"]`)
    })).filter(pair => pair.label);
    const preferred = [...new Set(groups
      .flatMap(group => group.options || [])
      .map(normalise)
      .filter(value => pairs.some(pair => normalise(pair.input.value) === value))
    )];
    preferred.forEach(value => {
      const pair = pairs.find(item => normalise(item.input.value) === value);
      if (pair) options.append(pair.input, pair.label);
    });
  };

  const hideOptionValueSummary = picker => {
    [...picker.querySelectorAll('fieldset')].forEach(field => {
      const name = field.querySelector('legend')?.textContent?.trim() || '';
      if (/^(color|gemstone):?$/i.test(name)) {
        field.querySelector('variant-option-value')?.setAttribute('hidden', '');
      }
    });
  };

  const alignQuickBuyDefault = (gallery, picker, groups) => {
    // Quick Buy is rendered through a detached product template, where
    // Shopify can default to the first database variant rather than the
    // first product-gallery group. Use the merchant-configured first group
    // as the one predictable starting point across product pages and modals.
    if (!gallery.closest('quick-buy-modal') || !groups[0]?.options?.length) return;
    syncNativeSelection(picker, groups[0].options);
  };

  const install = gallery => {
    installCounter(gallery);
    if (gallery.dataset.rcVariantGalleryReady === 'true') return;
    let groups;
    try { groups = JSON.parse(gallery.dataset.rcVariantGroups || '[]'); } catch { groups = []; }
    if (!Array.isArray(groups) || groups.length === 0) return;

    const picker = getPicker(gallery);
    if (!picker) return;
    gallery.dataset.rcVariantGalleryReady = 'true';
    blockRetiredControllers(picker);
    restoreMisappliedNativeOptions(picker);
    orderLighthouseMetal(picker, groups);
    hideOptionValueSummary(picker);
    alignQuickBuyDefault(gallery, picker, groups);
    const isQuickBuy = Boolean(gallery.closest('quick-buy-modal'));

    let frame = 0;
    let selectionPageY = null;
    let focusFirstImage = false;
    let repeatedOptionValue = null;
    const resetToGroupStart = (focusDocument = false) => {
      filterGallery(gallery, picker, groups, true, undefined, true);
      if (focusDocument && window.matchMedia('(min-width: 1000px)').matches) {
        const top = gallery.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: Math.max(0, top - 8), left: window.scrollX, behavior: 'instant' });
      }
    };
    const reinforceGroupStart = (focusDocument = false) => {
      // Prestige restores its native featured-media position asynchronously.
      // Re-apply only the current group's first cell after that work settles.
      [0, 180, 520].forEach(delay => window.setTimeout(() => resetToGroupStart(focusDocument), delay));
    };
    const settleNewProductAtTop = () => {
      // Prestige's native gallery can restore featured media during initial
      // hydration, which starts a newly opened product about one header-height
      // below the top. Correct that only for a new product-page navigation;
      // browser back/forward navigation keeps its normal scroll restoration.
      if (isQuickBuy || window.location.hash) return;
      const keepAtTop = () => {
        // Never interrupt a customer who has begun scrolling deliberately.
        if (window.scrollY <= 250) {
          window.scrollTo({ top: 0, left: window.scrollX, behavior: 'instant' });
        }
      };
      [0, 180, 520, 900, 1400].forEach(delay => window.setTimeout(keepAtTop, delay));
      if (document.readyState !== 'complete') window.addEventListener('load', keepAtTop, { once: true });
    };
    const update = (resetMobile, preservePagePosition = false, changedValue) => {
      if (preservePagePosition && selectionPageY === null) selectionPageY = window.scrollY;
      if (changedValue) focusFirstImage = true;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // Both "change" and "variant:change" fire for one customer action.
        // Keep the saved position through the second event instead of losing
        // it when the first scheduled frame is replaced.
        const pageY = selectionPageY;
        const shouldFocusFirstImage = focusFirstImage;
        selectionPageY = null;
        focusFirstImage = false;
        filterGallery(gallery, picker, groups, resetMobile, changedValue, shouldFocusFirstImage);
        if (pageY !== null) {
          requestAnimationFrame(() => {
            // Keep the current scroll position on desktop and mobile.
            // Switching product colors must not scroll the page.
            // Keep the first image selected while preserving the customer position.
            if (!isQuickBuy && shouldFocusFirstImage && window.matchMedia('(min-width: 1000px)').matches) {
              reinforceGroupStart(false);
            }
            if (Math.abs(window.scrollY - pageY) > 1) {
              window.scrollTo({ top: pageY, left: window.scrollX, behavior: 'instant' });
            }
          });
        }
      });
    };

    // One resolved change event per customer selection. The prior input +
    // change listeners applied the same mutation twice and caused flashing.
    picker.addEventListener('pointerdown', event => {
      const label = event.target.closest('label[for]');
      const input = label && picker.querySelector(`#${CSS.escape(label.htmlFor)}`);
      repeatedOptionValue = input?.checked ? input.value : null;
      if (event.target.closest('label, button')) selectionPageY = window.scrollY;
    }, true);
    picker.addEventListener('change', event => update(true, true, event.target?.value));
    picker.addEventListener('variant:change', () => update(true, true));
    if (gallery.dataset.rcProductHandle === 'breeze-pearl-bracelet') {
      const carousel = gallery.querySelector('.product-gallery__carousel');
      if (carousel) {
        let repairFrame = 0;
        const repair = () => {
          cancelAnimationFrame(repairFrame);
          repairFrame = requestAnimationFrame(() =>
            filterGallery(gallery, picker, groups, false)
          );
        };
        new MutationObserver(repair).observe(carousel, {
          attributes: true,
          subtree: true,
          attributeFilter: ['hidden']
        });
      }
    }
    picker.addEventListener('click', event => {
      const label = event.target.closest('label[for]');
      const input = label && picker.querySelector(`#${CSS.escape(label.htmlFor)}`);
      const wasAlreadySelected = input && repeatedOptionValue === input.value;
      repeatedOptionValue = null;
      // Clicking an already selected radio does not dispatch a native change
      // event. Still return the desktop gallery to that group's first image.
      if (wasAlreadySelected) update(true, true, input.value);
      if (event.target.closest('.rc-movement-linked-selectors, .rc-lighthouse-linked-selectors')) {
        // Use the capture phase because legacy linked selectors stop the
        // bubbling click event. A short task lets their visual state settle
        // before we synchronize the canonical native controls.
        window.setTimeout(() => {
          syncNativeSelection(picker, selectedValues(picker));
          update(true, true);
        }, 0);
      }
    }, true);
    window.addEventListener('resize', () => update(false), { passive: true });
    // On first load, reset the configured group to image 1 without moving the
    // document. A product link should always open at the browser's page top.
    resetToGroupStart(false);
    reinforceGroupStart(false);
    settleNewProductAtTop();
  };

  const boot = () => document.querySelectorAll('product-gallery[data-rc-variant-groups]').forEach(install);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  new MutationObserver(boot).observe(document.documentElement, { childList: true, subtree: true });
})();
